import Loading from "@components/loading/index.jsx";
import { Table } from "antd";
import moment from "moment";
import React, { useMemo } from "react";
import { formatDuration } from "@pages/profile-v2/utils";
import HistoryRangePicker from "@pages/profile-v2/pages/history-v2/components/RangePicker";
import AccountInsightsCompactEmpty from "../AccountInsightsCompactEmpty";
import AccountInsightsErrorState from "../AccountInsightsErrorState";
import AccountInsightsIntervalToggle from "../AccountInsightsIntervalToggle";
import { Line } from "react-chartjs-2";
import {
	ChartContainer,
	ChartLabel,
	ContentDivider,
	ControlsPanel,
	ControlsRow,
	DurationCell,
	EndReasonTag,
	InfiniteScrollSentinel,
	IpCell,
	ScrollableTableBody,
	SectionCard,
	SectionHeader,
	SectionTitle,
	StyledTableWrapper,
	TableBadge,
	TableSection,
	TableTitle,
	TableTitleRow,
} from "./elements";
import useController from "./useController";

const AccountInsightsSessionActivitySection = ({ translate }) => {
	const { state, handlers, infiniteScroll } = useController({ translate });

	const columns = useMemo(
		() => [
			{
				title: translate("accountInsights.sessionActivity.startTime"),
				dataIndex: "startTime",
				key: "startTime",
				width: 150,
				render: (value) =>
					value ? moment.unix(value).format("DD.MM.YYYY HH:mm") : "—",
			},
			{
				title: translate("accountInsights.sessionActivity.endTime"),
				dataIndex: "endTime",
				key: "endTime",
				width: 150,
				render: (value) =>
					value ? moment.unix(value).format("DD.MM.YYYY HH:mm") : "—",
			},
			{
				title: translate("accountInsights.sessionActivity.duration"),
				dataIndex: "durationSeconds",
				key: "durationSeconds",
				width: 100,
				render: (value) => (
					<DurationCell>{formatDuration(value, translate)}</DurationCell>
				),
			},
			{
				title: translate("accountInsights.sessionActivity.endReason"),
				dataIndex: "endReason",
				key: "endReason",
				width: 110,
				render: (value) => (
					<EndReasonTag $variant={value}>
						{handlers.getEndReasonLabel(value)}
					</EndReasonTag>
				),
			},
			{
				title: translate("accountInsights.sessionActivity.ipAddress"),
				dataIndex: "ipAddress",
				key: "ipAddress",
				render: (value) => <IpCell>{value || "—"}</IpCell>,
			},
		],
		[handlers, translate]
	);

	return (
		<SectionCard>
			<SectionHeader>
				<SectionTitle>{translate("accountInsights.sessionActivity.title")}</SectionTitle>
				<ControlsPanel>
					<HistoryRangePicker
						translate={translate}
						onChange={handlers.setDateRange}
					/>
					<ControlsRow>
						<AccountInsightsIntervalToggle
							value={state.interval}
							onChange={handlers.setInterval}
							translate={translate}
						/>
					</ControlsRow>
				</ControlsPanel>
			</SectionHeader>

			{state.isLoading && <Loading loading />}

			{state.isError && !state.isLoading && (
				<AccountInsightsErrorState
					translate={translate}
					onRetry={handlers.refetch}
				/>
			)}

			{!state.isLoading && !state.isError && (
				<>
					{state.buckets.length > 0 ? (
						<div>
							<ChartLabel>
								{translate("accountInsights.sessionActivity.chartLabel")}
							</ChartLabel>
							<ChartContainer $height={300}>
								<Line
									data={state.durationChart.data}
									options={state.durationChart.options}
								/>
							</ChartContainer>
						</div>
					) : (
						<AccountInsightsCompactEmpty
							translate={translate}
							title="accountInsights.empty.chartTitle"
							description="accountInsights.empty.chartDescription"
						/>
					)}

					{state.sessions.length > 0 && <ContentDivider />}

					{state.sessions.length > 0 ? (
						<TableSection>
							<TableTitleRow>
								<TableTitle>
									{translate("accountInsights.sessionActivity.tableTitle")}
								</TableTitle>
								<TableBadge>
									{translate("accountInsights.table.loadedCount", {
										count: state.sessions.length,
									})}
								</TableBadge>
							</TableTitleRow>
							<StyledTableWrapper>
								<ScrollableTableBody innerRef={infiniteScroll.scrollRootRef}>
									<Table
										columns={columns}
										dataSource={state.tableData}
										loading={state.tableLoading && !state.sessions.length}
										pagination={false}
										scroll={{ x: 760 }}
										size="middle"
									/>
									{state.showSentinel && (
										<InfiniteScrollSentinel innerRef={infiniteScroll.sentinelRef}>
											{state.tableLoading
												? translate("accountInsights.table.loadingMore")
												: translate("accountInsights.table.scrollForMore")}
										</InfiniteScrollSentinel>
									)}
								</ScrollableTableBody>
							</StyledTableWrapper>
						</TableSection>
					) : (
						<AccountInsightsCompactEmpty
							translate={translate}
							title="accountInsights.empty.sessionsTitle"
							description="accountInsights.empty.sessionsDescription"
						/>
					)}
				</>
			)}
		</SectionCard>
	);
};

export default AccountInsightsSessionActivitySection;
